import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { type TargetQueryV4Row } from "../api/target/v4TargetReportByTarget";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../redux/hooks";
import { formatCurrency } from "../../../utils/formatNumber";
import { type GVAdvertisement } from "../../advertising/types";
import { useBaseTargetColumns } from "./useBaseTargetColumns";
import { TargetTextCell } from "./TargetTextCell";
import { useModuleEnabled } from "../../../redux/selectors/modules";

const columnHelper = createColumnBuilder<TargetQueryV4Row>();

export const useTargetReportColumns = (targetType: string) => {
  const { t } = useTranslation(["perf_reports"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const restColumns = useBaseTargetColumns(targetType);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  return useMemo(() => {
    const textColumn = columnHelper.accessor("text", {
      id: "text",
      header: t(($) => $.perf_reports["Text"]),
      enableHiding: false,
      minSize: 100,
      size: 300,
      cell: (props) => {
        const adsMapping = (props.table.options.meta as { adsMapping?: Record<string, GVAdvertisement> }).adsMapping;
        return <TargetTextCell target={props.row.original} adsMapping={adsMapping} />;
      },
    });

    return [
      textColumn,
      // Invisible columns to support filtering
      columnHelper.accessor((row) => row.ad_id, {
        id: "ad_id",
        header: t(($) => $.perf_reports["Media Plan"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "media-plan",
          },
        },
      }),
      columnHelper.accessor((row) => row.category_id, {
        id: "category_id",
        header: t(($) => $.perf_reports["Category"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row.tags, {
        id: tagsEnhancedEnabled ? "tags_enhanced.id" : "tags",
        header: t(($) => $.perf_reports["Tags"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select" as const,
            label: t(($) => $.perf_reports["Tags"]),
          },
        },
      }),
      // Invisible columns end
      columnHelper.accessor("bid_resolved", {
        id: "bid_resolved",
        header: t(($) => $.perf_reports["Bid"]),
        size: 70,
        cell: ({ getValue }) => {
          const bid = getValue();

          if (bid) {
            return formatCurrency(bid, {
              marketPlace: marketPlace,
            });
          }
          return null;
        },
        meta: {
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The current bid for the advertised {targetType}"], { targetType }),
          },
          columnFilterOptions: {
            format: "currency",
          },
        },
      }),
      ...restColumns,
    ];
  }, [restColumns, marketPlace, targetType, tagsEnhancedEnabled, t]);
};
